import { Injectable, Injector } from '@angular/core';
import { catchError, EMPTY, finalize, first, map, Observable, of, retry, tap, throwError, timer } from 'rxjs';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { environment } from 'src/environments/environment';
import { LoggerService } from 'src/app/logger/logger.service';
import { CollaborationLoggerService } from './collaboration-logger.service';
import { CollaborationNavigationService } from './collaboration-navigation.service';
import { CollaborationTokenStoreService } from './collaboration-token-store.service';
import { ApiClientCollaborationAssessmentsService } from '../api/api-client-collaboration-assessments.service';
import { AssessmentInviteeStatusEnum } from 'src/app/api/models/assessments/assessment.enum';
import { MineSnackbarService } from 'src/app/shared/mine-snackbar/mine-snackbar.service';
import { MineSnackbarType } from 'src/app/shared/mine-snackbar/mine-snackbar-type';

@Injectable({
	providedIn: 'root',
})
export class CollaborationHttpErrorsInterceptor implements HttpInterceptor {
	private readonly loggerName: string = 'HttpErrorsInterceptor';
	private readonly logOptions = { component: 'CollaborationHttpErrorsInterceptor' };
	private isCheckingRevoke = false;

	constructor(
		private injector: Injector,
		private logger: LoggerService,
		private collabLogger: CollaborationLoggerService,
	) {}

	private get navigationService(): CollaborationNavigationService {
		return this.injector.get(CollaborationNavigationService);
	}

	private get snackbarService(): MineSnackbarService {
		return this.injector.get(MineSnackbarService);
	}

	private getErrorMessage(error: HttpErrorResponse): string {
		if (typeof error.error === 'string') {
			return error.error;
		}
		return error.error?.message ?? error.message ?? 'An error occurred';
	}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(req).pipe(
			catchError((error: HttpErrorResponse) => {
				this.collabLogger.error(
					'HTTP error occurred',
					{
						status: error.status,
						statusText: error.statusText,
						requestUrl: req.url,
						method: req.method,
						error: error.message,
					},
					this.logOptions,
				);

				if (error.status >= 400 && error.status < 500) {
					return this.handleClientErrors(error);
				} else if (error.status >= 500 || error.status == 0) {
					return this.handleServerErrors(req, next);
				} else {
					this.collabLogger.logError(
						error,
						{
							error: error.message,
							errorStack: error.error?.stack,
							status: error.status,
							requestUrl: req.url,
							method: req.method,
						},
						this.logOptions,
					);
					return throwError(() => error);
				}
			}),
		);
	}

	private handleClientErrors(error: HttpErrorResponse): Observable<void> {
		const tokenStore = this.injector.get(CollaborationTokenStoreService);
		const assessmentId = tokenStore.getActiveAssessmentId();
		const collaboratorEmail = tokenStore.getCollaboratorEmail();

		// Missing token store data - always navigate (critical auth issue)
		if (!assessmentId || !collaboratorEmail) {
			return of(this.navigationService.navigateToAuthenticationErrorPage(error));
		}

		// If already checking revoke status, navigate
		if (this.isCheckingRevoke) {
			return of(this.navigationService.navigateToAuthenticationErrorPage(error));
		}

		this.isCheckingRevoke = true;

		return this.injector
			.get(ApiClientCollaborationAssessmentsService)
			.getAssessments()
			.pipe(
				first(),
				tap((assessments) => {
					const instance = assessments.find((assessment) => assessment.id == assessmentId);
					if (!instance) {
						this.navigationService.navigateToDeletedAssessmentPage();
						return EMPTY;
					}
					const invite = instance.collabInvites?.find((inv) => inv.email === collaboratorEmail);
					if (invite?.status === AssessmentInviteeStatusEnum.Revoked) {
						// Access revoked - navigate to auth error page
						this.navigationService.navigateToAuthenticationErrorPage(
							new HttpErrorResponse({ status: 403, statusText: 'Forbidden', error: 'Access revoked' }),
						);
					} else {
						this.snackbarService.showTimed(MineSnackbarType.Error, this.getErrorMessage(error));
					}
				}),
				catchError((error) => {
					this.snackbarService.showTimed(MineSnackbarType.Error, this.getErrorMessage(error));
					return EMPTY;
				}),
				finalize(() => {
					this.isCheckingRevoke = false;
				}),
				map(() => void 0),
			);
	}

	private handleServerErrors(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
		const maxRetries = environment.api.errorHandling.maxNumOfRetries;
		let retryDelay = 500;

		return next.handle(req).pipe(
			retry({
				count: maxRetries - 1,
				delay: (err: HttpErrorResponse, retryCount: number) => {
					this.logger.error(this.loggerName, `Retry number ${retryCount} for http call: ${err.url}`);
					this.collabLogger.warn(
						`Retrying HTTP request (attempt ${retryCount})`,
						{
							requestUrl: err.url,
							method: req.method,
							retryNumber: retryCount,
							maxRetries,
						},
						this.logOptions,
					);
					const delayMs = retryDelay;
					retryDelay *= 2;
					return timer(delayMs);
				},
			}),
			catchError((err) => {
				this.handleMaxRetryError(err);
				return throwError(() => err);
			}),
		);
	}

	private handleMaxRetryError(error: HttpErrorResponse): void {
		this.collabLogger.logError(
			error,
			{
				error: error.message,
				errorStack: error.error?.stack,
				status: error.status,
				statusText: error.statusText,
				requestUrl: error.url,
				maxRetriesReached: true,
			},
			this.logOptions,
		);

		this.snackbarService.showTimed(MineSnackbarType.Error, this.getErrorMessage(error));
	}
}
